import React, { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3 } from "lucide-react";

const sb: any = supabase as any;

type DomainScore = {
  domain_name: string;
  average_score: number;
  maturity_level: string;
  controls_evaluated: number;
};

const CSV_DOMAINS = [
  "Risk Management",
  "Governance",
  "Supply Chain Management",
  "Physical & Environmental Protection",
  "Asset Management",
  "Identity-Centric Security",
  "Security Operations",
] as const;

const NOT_ASSESSED = "Not Assessed";

const DEFAULT_SCORE: Omit<DomainScore, "domain_name"> = {
  average_score: 0,
  maturity_level: NOT_ASSESSED,
  controls_evaluated: 0,
};

const MATURITY_COLORS: Record<string, string> = {
  "Not Assessed": "#6B7280", // slate-500 — neutral
  Minimal: "#DC2626",
  Adequate: "#F59E0B",
  Strong: "#16A34A",
  Extensive: "#2563EB",
};

type Props = {
  assessmentId: string;
  refreshKey?: number;
  onOverallScoreChange?: (score: number) => void;
  /** When true, hide numeric score and controls-evaluated lines. */
  hideNumeric?: boolean;
};

export function CsvDomainScorecard({ assessmentId, refreshKey, onOverallScoreChange, hideNumeric }: Props) {
  const [dbScores, setDbScores] = useState<DomainScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadScores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await sb.rpc("calculate_csv_domain_scores", {
        p_assessment_id: assessmentId,
      });
      if (rpcError) throw rpcError;
      setDbScores((Array.isArray(data) ? data : []) as DomainScore[]);
    } catch (e: any) {
      console.error("[CsvDomainScorecard] load error:", e);
      setError(e.message ?? "Failed to load CSV domain scores");
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    loadScores();
  }, [loadScores, refreshKey]);

  // Always render all 7 domains, merging DB results with defaults
  const scores = useMemo(() => {
    const map = new Map(dbScores.map((s) => [s.domain_name, s]));
    return CSV_DOMAINS.map((dn) => ({
      ...DEFAULT_SCORE,
      domain_name: dn,
      ...(map.get(dn) ?? {}),
    }));
  }, [dbScores]);

  // Calculate and emit overall score
  useEffect(() => {
    if (!onOverallScoreChange) return;
    const scored = scores.filter((s) => s.controls_evaluated > 0);
    if (scored.length === 0) {
      onOverallScoreChange(0);
      return;
    }
    const overall = scored.reduce((sum, s) => sum + Number(s.average_score), 0) / scored.length;
    onOverallScoreChange(overall);
  }, [scores, onOverallScoreChange]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground text-sm">
          Calculating CSV domain scores…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-destructive">
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="h-4 w-4 text-primary" />
          CSV Cybersecurity Domain Ratings
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {scores.map((s) => (
            <div
              key={s.domain_name}
              className="rounded-lg border bg-card p-4 flex flex-col items-center gap-1"
            >
              <span className="text-[11px] font-medium text-muted-foreground text-center leading-tight min-h-[2rem] flex items-center">
                {s.domain_name}
              </span>
              <span
                className="text-lg font-bold"
                style={{ color: MATURITY_COLORS[s.maturity_level] ?? "hsl(var(--foreground))" }}
              >
                {s.maturity_level}
              </span>
              {!hideNumeric && s.controls_evaluated > 0 && (
                <>
                  <span className="text-xs text-muted-foreground">
                    Score: <strong className="text-foreground">{Number(s.average_score).toFixed(2)}</strong>
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {s.controls_evaluated} controls evaluated
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
